import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { formatMeterExtensionLabel } from '../../../utils/meterMappingUtils';
import './PatientStatusDeviceRemovalModal.css';

/**
 * Modal to confirm permanent unassignment of devices from DB and ForaCare
 * when changing a patient's status from Active to Pending or Locked.
 *
 * @param {Object} props
 * @param {boolean} props.show
 * @param {string} props.patientName
 * @param {string} props.targetStatus - e.g. "Pending" or "Locked"
 * @param {Array} props.devices - Array of device/meter objects
 * @param {Function} props.onConfirm
 * @param {Function} props.onCancel
 * @param {boolean} [props.isSubmitting]
 */
export default function PatientStatusDeviceRemovalModal({
  show,
  patientName = 'this patient',
  targetStatus = 'Pending',
  devices = [],
  onConfirm,
  onCancel,
  isSubmitting = false
}) {
  const confirmBtnRef = useRef(null);

  useEffect(() => {
    if (!show) return undefined;

    const onKeyDown = (e) => {
      if (e.key === 'Escape' && !isSubmitting) {
        onCancel?.();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Focus confirm button when modal opens
    setTimeout(() => {
      confirmBtnRef.current?.focus();
    }, 50);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [show, isSubmitting, onCancel]);

  if (!show) return null;

  const validDevices = (Array.isArray(devices) ? devices : []).filter((d) => {
    const sn = String(d?.serial_number ?? d?.serialNumber ?? '').trim();
    const mn = String(d?.model_number ?? d?.modelNumber ?? '').trim();
    return Boolean(sn || mn);
  });

  const modalContent = (
    <div
      className="psdrm-overlay"
      role="presentation"
      onClick={() => {
        if (!isSubmitting) onCancel?.();
      }}
    >
      <div
        className="psdrm-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="psdrm-title"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="psdrm-header">
          <div className="psdrm-header-text">
            <h5 id="psdrm-title" className="psdrm-title">
              Assigned Devices Will Be Removed
            </h5>
            
          </div>
          <button
            type="button"
            className="psdrm-close-btn"
            onClick={onCancel}
            disabled={isSubmitting}
            aria-label="Close"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>

        {/* Body */}
        <div className="psdrm-body">
          {/* Devices List Table */}
          <div className="psdrm-devices-section">
            <div className="psdrm-devices-section-title">
              <span>Assigned Devices ({validDevices.length})</span>
            </div>

            {validDevices.length === 0 ? (
              <p className="psdrm-no-devices">No specific devices found.</p>
            ) : (
              <div className="psdrm-table-container">
                <table className="psdrm-devices-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Model Number</th>
                      <th>Extension / Type</th>
                      <th>Serial Number</th>
                      <th>Gateway Serial</th>
                    </tr>
                  </thead>
                  <tbody>
                    {validDevices.map((dev, idx) => {
                      const modelNum = dev.model_number || dev.modelNumber || '—';
                      const extRaw = dev.extension ?? '';
                      const extLabel = formatMeterExtensionLabel(extRaw);
                      const serial = dev.serial_number || dev.serialNumber || '—';
                      const gateway = dev.gateway_serial_number || dev.gatewaySerialNumber || '—';

                      return (
                        <tr key={dev.id || dev._localKey || `dev-${idx}`}>
                          <td className="psdrm-col-idx">{idx + 1}</td>
                          <td className="psdrm-col-model">
                            <strong>{modelNum}</strong>
                          </td>
                          <td className="psdrm-col-ext">
                            <span className="psdrm-ext-badge">{extLabel}</span>
                          </td>
                          <td className="psdrm-col-serial">
                            <code className="psdrm-serial-code">{serial}</code>
                          </td>
                          <td className="psdrm-col-gateway">
                            {gateway !== '—' ? <code className="psdrm-serial-code">{gateway}</code> : <span className="psdrm-muted-dash">—</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="psdrm-footer">
          <button
            type="button"
            className="psdrm-btn psdrm-btn-cancel"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            ref={confirmBtnRef}
            className="psdrm-btn psdrm-btn-confirm"
            onClick={onConfirm}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Removing Devices...' : `Remove Devices & Set to ${targetStatus}`}
          </button>
        </div>
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(modalContent, document.body) : modalContent;
}
